import { createContext, type ReactNode, useContext } from "react";
import { createGateCodeQueryOptionsFactory } from "@/queries/gateCodes.ts";
import { createOwnerQueryOptionsFactory } from "@/queries/owners.ts";
import { createReservationQueryOptionsFactory } from "@/queries/reservations.ts";
import { createUnitQueryOptionsFactory } from "@/queries/units.ts";

export const createQueryOptionsFactory = (authFetch: typeof fetch) => ({
    reservations: createReservationQueryOptionsFactory(authFetch),
    units: createUnitQueryOptionsFactory(authFetch),
    owners: createOwnerQueryOptionsFactory(authFetch),
    gateCodes: createGateCodeQueryOptionsFactory(authFetch),
});

export type QueryOptionsFactory = ReturnType<typeof createQueryOptionsFactory>;

const QueryOptionsFactoryContext = createContext<QueryOptionsFactory | null>(null);

type QueryOptionsFactoryProviderProps = {
    children: ReactNode;
    factory: QueryOptionsFactory;
};

export const QueryOptionsFactoryProvider = ({
    children,
    factory,
}: QueryOptionsFactoryProviderProps): ReactNode => (
    <QueryOptionsFactoryContext.Provider value={factory}>
        {children}
    </QueryOptionsFactoryContext.Provider>
);

export const useQueryOptionsFactory = (): QueryOptionsFactory => {
    const factory = useContext(QueryOptionsFactoryContext);

    if (!factory) {
        throw new Error("useQueryOptionsFactory used outside QueryOptionsFactoryProvider");
    }

    return factory;
};
